import { DISPLAY_METHOD_TAX_OPTIONS } from "../../../../../../../utils/Constants";
import { getPaidQuantityForLine, type LineIdentity } from "../hooks/splitItemIdentity.ts";

export interface SplitItem {
  price?: number;
  total_price?: number;
  split_quantity: number;
  tax_price?: number;
  consumption_tax_rate?: number;
}

type LineIdentitySource = {
  _id?: unknown;
  menu_item_id?: unknown;
  order_id?: unknown;
  source_order_id?: unknown;
  ordered_line_id?: unknown;
  temp_item_id?: unknown;
  price?: unknown;
  consumption_tax_rate?: unknown;
};

export type PaidOrderItem = LineIdentitySource & { quantity?: number };

/**
 * Returns the quantity already paid for one ordered line. Split-by-items
 * stores paid rows separately for the base order and each sub-order, so the
 * full line identity (getPaidQuantityForLine, mirroring the backend's
 * LineIdentity/paidRowMatches in splitByItems.ts) must be used instead of
 * matching by menu item alone. Pass `knownIdentities` (distinctIdentities
 * over every line on the order) so a legacy paid row without
 * ordered_line_id resolves unambiguously rather than pooling onto every
 * line sharing the same menu item/price/tax; defaults to just this line
 * when the caller has no broader context.
 */
export const getPaidQuantityForItem = (
  item: LineIdentitySource,
  paidItems: PaidOrderItem[] = [],
  knownIdentities?: LineIdentity[],
): number => getPaidQuantityForLine(item, paidItems, knownIdentities);
// A type alias, not an interface: aliases get an implicit index signature, so
// an add-on stays assignable to the Record<string, unknown> parameter the
// callers' getTranslatedKey wrappers declare.
export type SpiceLevelDoc = {
  _id?: string;
  name?: string;
  name_ja?: string;
};

export type OrderItemAddon = {
  item_id?: string;
  item_name?: string;
  item_name_ja?: string;
  // The component's own catalogue price. Informational only — order totals are
  // computed from the parent line's price and never walk addons
  // (global-functions.js orderRecalculationCashier).
  price?: number;
  total_price?: number;
  // A set-menu component's chosen spice is stored as the whole spice document
  // (CashierMenu.js:58 → setMenuAddons.js:32), or 0 when the component takes
  // no spice. Never a bare id.
  spice_level?: string | number | SpiceLevelDoc | null;
};

const asSpiceDoc = (spiceLevel: unknown): SpiceLevelDoc | null =>
  typeof spiceLevel === "object" && spiceLevel !== null ? spiceLevel as SpiceLevelDoc : null;

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? value as Record<string, unknown> : {};

/**
 * The line's own chosen spice level, localized, or null when it has none. A
 * line-level configuration discriminator: two lines of the same menu item can
 * differ only by this (CashierMenu.js reopens the picker for every add), so any
 * list that asks the cashier to pick a line must show it or the rows are
 * indistinguishable. Only ever a spice document — `0` / a bare id means "no
 * spice chosen" and yields null.
 */
export const getSpiceLevelName = (
  item: unknown,
  translate: (key: string, data?: SpiceLevelDoc) => string,
): string | null => {
  const spiceDoc = asSpiceDoc(asRecord(item).spice_level);
  return spiceDoc ? translate("name", spiceDoc) || null : null;
};

/**
 * Display labels for a line's add-ons. Names come from the authoritative menu
 * doc the backend spreads onto each add-on (buildCashierOrderItem.js:78), so
 * `translate` is the caller's locale picker (getTranslatedKey) — used for the
 * add-on's own name and, for a set-menu component, its chosen spice level.
 */
export const getAddonLabels = (
  item: unknown,
  translate: (key: string, data?: OrderItemAddon | SpiceLevelDoc) => string,
): string[] => getAddonRows(item, translate).map((row) => row.label);

export type AddonDisplayRow = {
  label: string;
  price: number | null;
};

/**
 * One display row per add-on: its localized name (with the chosen spice for a
 * set-menu component) and its own catalogue price.
 *
 * `taxExcluded` mirrors the restaurant's display_method_product_price so the
 * add-on price is quoted on the same basis as the line price above it. The
 * price is shown for information only — it is not part of what the customer is
 * charged, which comes solely from the parent line.
 */
export const getAddonRows = (
  item: unknown,
  translate: (key: string, data?: OrderItemAddon | SpiceLevelDoc) => string,
  taxExcluded = false,
): AddonDisplayRow[] => {
  const itemRecord = asRecord(item);
  const addons = Array.isArray(itemRecord.addons)
    ? itemRecord.addons as OrderItemAddon[]
    : [];

  return addons
    .map((addon) => {
      const name = translate("item_name", addon);
      if (!name) {
        return null;
      }
      const spiceDoc = asSpiceDoc(addon?.spice_level);
      const spiceName = spiceDoc ? translate("name", spiceDoc) : "";
      const price = taxExcluded ? addon?.price : addon?.total_price;
      return {
        label: spiceName ? `${name} (${spiceName})` : name,
        price: typeof price === "number" ? price : null,
      };
    })
    .filter((row): row is AddonDisplayRow => row !== null);
};

/**
 * The parent line price includes the selected add-ons. Keep the full line
 * price for the amount charged, while exposing the menu item's own price for
 * display alongside its name.
 */
export const getBaseMenuItemPrice = (
  item: unknown,
  translate: (key: string, data?: OrderItemAddon | SpiceLevelDoc) => string,
  taxExcluded = false,
): number => {
  const itemRecord = asRecord(item);
  const displayedLinePrice = taxExcluded
    ? itemRecord.price ?? itemRecord.total_price ?? 0
    : itemRecord.total_price ?? itemRecord.price ?? 0;
  const displayedAddonPrice = getAddonRows(item, translate, taxExcluded).reduce(
    (total, row) => total + (row.price ?? 0),
    0,
  );

  return Math.max(0, Number(displayedLinePrice) - displayedAddonPrice);
};

export interface OrderPriceDetail {
  service_charge?: number;
  subtotal?: number;
  coupon_type?: string;
  coupon_discount?: number;
}

export interface RestSettingDetails {
  display_method_product_price?: string;
}

interface CalculateSplitTotalParams {
  splitItems?: SplitItem[];
  orderPriceDetail?: OrderPriceDetail;
  couponCodeApplied?: boolean;
  restSettingDetails?: RestSettingDetails;
}

export interface SplitTotalResult {
  subtotal: number;
  tax_price: number;
  tax_8: number;
  tax_10: number;
  service_charge: number;
  great_grand_total: number;
  coupon_discount: number;
  coupon_type: string | undefined;
}

export interface EqualSplitDistribution {
  currentShare: number;
  lowerShare: number;
  higherShare: number;
  lowerShareCount: number;
  higherShareCount: number;
  remainder: number;
  dividesEvenly: boolean;
}

interface ItemTotalsAccumulator {
  subtotal: number;
  tax_8: number;
  tax_10: number;
}

const calculateItemPrice = (item: SplitItem, restSettingDetails?: RestSettingDetails): number => {
  return restSettingDetails?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED
    ? Number(item.price ?? 0) * item.split_quantity
    : Number(item.total_price ?? 0) * item.split_quantity;
};

const calculateItemTax = (item: SplitItem, taxRate: number): number => {
  return item.consumption_tax_rate === taxRate
    ? ((item.tax_price ?? 0) + (item.price ?? 0)) * item.split_quantity
    : 0;
};

const calculateCouponDiscount = (
  beforeDiscount: number,
  orderPriceDetail?: OrderPriceDetail
): number => {
  if (!orderPriceDetail) return 0;

  if (orderPriceDetail.coupon_type === "percentage") {
    return (beforeDiscount * (orderPriceDetail.coupon_discount ?? 0)) / 100;
  }

  if (orderPriceDetail.coupon_type === "fixed") {
    return Math.min(Number(orderPriceDetail.coupon_discount ?? 0), beforeDiscount);
  }

  return 0;
};

export const calculateSplitTotal = ({
  splitItems = [],
  orderPriceDetail,
  couponCodeApplied,
  restSettingDetails,
}: CalculateSplitTotalParams): SplitTotalResult => {
  const totals = splitItems.reduce<ItemTotalsAccumulator>(
    (acc, item) => ({
      subtotal: acc.subtotal + calculateItemPrice(item, restSettingDetails),
      tax_8: acc.tax_8 + calculateItemTax(item, 8),
      tax_10: acc.tax_10 + calculateItemTax(item, 10),
    }),
    { subtotal: 0, tax_8: 0, tax_10: 0 }
  );

  const { subtotal, tax_8, tax_10 } = totals;
  const tax_price = tax_8 + tax_10 - subtotal;

  const service_charge = orderPriceDetail?.service_charge
    ? (subtotal / (orderPriceDetail?.subtotal || 1)) * orderPriceDetail.service_charge
    : 0;

  const beforeDiscount = subtotal + service_charge + tax_price;
  const coupon_discount = couponCodeApplied
    ? calculateCouponDiscount(beforeDiscount, orderPriceDetail)
    : 0;

  return {
    subtotal,
    tax_price,
    tax_8,
    tax_10,
    service_charge,
    great_grand_total: beforeDiscount - coupon_discount,
    coupon_discount,
    coupon_type: orderPriceDetail?.coupon_type,
  };
};

export const getEqualSplitDistribution = (
  remainingAmount: number,
  peopleCount: number
): EqualSplitDistribution => {
  if (remainingAmount < 0 || !Number.isInteger(remainingAmount)) {
    throw new Error("Remaining amount must be a non-negative integer.");
  }
  if (peopleCount < 1 || !Number.isInteger(peopleCount)) {
    throw new Error("People count must be a positive integer.");
  }

  const totalAmount = remainingAmount;
  const totalPeople = peopleCount;

  const lowerShare = Math.floor(totalAmount / totalPeople);
  const remainder = totalAmount % totalPeople;
  const dividesEvenly = remainder === 0;
  const higherShare = dividesEvenly ? lowerShare : lowerShare + 1;
  const higherShareCount = dividesEvenly ? totalPeople : remainder;
  const lowerShareCount = dividesEvenly ? 0 : totalPeople - remainder;
  const currentShare = dividesEvenly ? lowerShare : higherShare;

  return {
    currentShare,
    lowerShare,
    higherShare,
    lowerShareCount,
    higherShareCount,
    remainder,
    dividesEvenly,
  };
};
